import React, { useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Form, FormInstance, Schema, SelectPicker,
} from 'rsuite';
import _ from 'lodash';
import axios from 'axios';
import {
  AllegroCreateResponsibleProducerRequest,
  AllegroResponsibleProducer,
  AllegroUpdateResponsibleProducerRequest,
} from '../../../../api/allegro/responsibleproducer/responsibleProducerTypes';
import { createAllegroResponsibleProducer, updateAllegroResponsibleProducer } from '../../../../api/allegro/responsibleproducer/responsibleProducer';
import { showErrorMessage, showSuccessMessage } from '../../../../utils/message';
import { ALL_COUNTRY_CODES } from '../../../../utils/countries';

const COUNTRY_CODES = ALL_COUNTRY_CODES.map((countryCode) => ({
  value: countryCode,
  label: countryCode,
}));

const validationModel = Schema.Model({
  label: Schema.Types.StringType()
    .isRequired('Label is required.')
    .maxLength(50, 'Max 50 characters.'),
  tradeName: Schema.Types.StringType()
    .isRequired('Trade name is required.')
    .maxLength(200, 'Max 200 characters.'),
  street: Schema.Types.StringType()
    .isRequired('Street is required.')
    .maxLength(200, 'Max 200 characters.'),
  postalCode: Schema.Types.StringType()
    .isRequired('Postal code is required.')
    .maxLength(20, 'Max 20 characters.'),
  city: Schema.Types.StringType()
    .isRequired('City is required.')
    .maxLength(100, 'Max 100 characters.'),
  countryCode: Schema.Types.StringType().isRequired('Country code is required.'),
  email: Schema.Types.StringType()
    .maxLength(50, 'Max 50 characters.')
    .isEmail('Email is not valid.')
    .addRule(
      (value, edit: Edit) => !(_.isEmpty(value) && _.isEmpty(edit.formUrl)),
      'Email or form URL is required.',
      true,
    ),
  phoneNumber: Schema.Types.StringType().maxLength(30, 'Max 30 characters.'),
  formUrl: Schema.Types.StringType()
    .maxLength(80, 'Max 80 characters.')
    .addRule(
      (value, edit: Edit) => !(_.isEmpty(value) && _.isEmpty(edit.email)),
      'Email or form URL is required.',
      true,
    ).addRule(
      (value) => _.isEmpty(value) || value.startsWith('https://'),
      'Form URL must start with "https://".',
    ),
});

interface Props {
  marketplaceCode: string;
  responsibleProducer?: AllegroResponsibleProducer;
  onCancel: () => void;
  onSaved: () => void;
}

type Status = 'ready' | 'saving';

interface Edit {
  label: string;
  tradeName: string;
  street: string;
  postalCode: string;
  city: string;
  countryCode: string | null;
  email: string;
  phoneNumber: string;
  formUrl: string;
}

interface State {
  status: Status;
  edit: Edit;
}

const initialEdit: Edit = {
  label: '',
  tradeName: '',
  street: '',
  postalCode: '',
  city: '',
  countryCode: null,
  email: '',
  phoneNumber: '',
  formUrl: '',
};

function getInitialState(responsibleProducer?: AllegroResponsibleProducer): State {
  if (!responsibleProducer) {
    return {
      status: 'ready',
      edit: initialEdit,
    };
  }

  return {
    status: 'ready',
    edit: {
      label: responsibleProducer.name,
      tradeName: responsibleProducer.producerData.tradeName,
      street: responsibleProducer.producerData.address.street,
      postalCode: responsibleProducer.producerData.address.postalCode,
      city: responsibleProducer.producerData.address.city,
      countryCode: responsibleProducer.producerData.address.countryCode,
      email: responsibleProducer.producerData.contact.email ?? '',
      phoneNumber: responsibleProducer.producerData.contact.phoneNumber ?? '',
      formUrl: responsibleProducer.producerData.contact.formUrl ?? '',
    },
  };
}

type EditSetAction = { type: 'edit/set', payload: Edit };
type StatusSetAction = { type: 'status/set', payload: Status };
type Action = EditSetAction | StatusSetAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'edit/set': {
      return { ...state, edit: action.payload };
    }
    case 'status/set': {
      return { ...state, status: action.payload };
    }
    default:
      return state;
  }
}

function mapCreateRequest(
  edit: Edit,
): AllegroCreateResponsibleProducerRequest {
  return {
    name: edit.label,
    producerData: {
      tradeName: edit.tradeName,
      address: {
        countryCode: edit.countryCode ?? '',
        street: edit.street,
        postalCode: edit.postalCode,
        city: edit.city,
      },
      contact: {
        email: edit.email || undefined,
        phoneNumber: edit.phoneNumber || undefined,
        formUrl: edit.formUrl || undefined,
      },
    },
  };
}

function mapUpdateRequest(
  responsibleProducer: AllegroResponsibleProducer,
  edit: Edit,
): AllegroUpdateResponsibleProducerRequest {
  return {
    id: responsibleProducer.id,
    ...mapCreateRequest(edit),
  };
}

export default function EshopSetupAllegroResponsibleProducerEdit({
  marketplaceCode,
  responsibleProducer,
  onCancel,
  onSaved,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, responsibleProducer, getInitialState);
  const formRef = useRef<FormInstance>(null);

  async function saveResponsibleProducer() {
    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      const { edit } = state;

      if (responsibleProducer) {
        await updateAllegroResponsibleProducer(
          marketplaceCode,
          responsibleProducer.id,
          mapUpdateRequest(responsibleProducer, edit),
        );
      } else {
        await createAllegroResponsibleProducer(
          marketplaceCode,
          mapCreateRequest(edit),
        );
      }

      showSuccessMessage('Responsible producer successfully saved.');
      onSaved();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showErrorMessage(JSON.stringify(error.response?.data));
      }
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleSave() {
    if (!formRef.current?.check()) {
      return;
    }

    saveResponsibleProducer().then();
  }

  return (
    <Form
      ref={formRef}
      fluid
      formValue={state.edit}
      onChange={(edit) => dispatch({ type: 'edit/set', payload: edit as Edit })}
      model={validationModel}
      layout="vertical"
    >
      <Form.Group>
        <Form.ControlLabel>Label</Form.ControlLabel>
        <Form.Control name="label" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Trade name</Form.ControlLabel>
        <Form.Control name="tradeName" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Street</Form.ControlLabel>
        <Form.Control name="street" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Postal code</Form.ControlLabel>
        <Form.Control name="postalCode" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>City</Form.ControlLabel>
        <Form.Control name="city" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Country code</Form.ControlLabel>
        <Form.Control
          name="countryCode"
          accepter={SelectPicker}
          data={COUNTRY_CODES}
          cleanable={false}
          searchable
          block
        />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Email</Form.ControlLabel>
        <Form.Control name="email" type="email" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Phone number</Form.ControlLabel>
        <Form.Control name="phoneNumber" type="tel" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Form URL</Form.ControlLabel>
        <Form.Control name="formUrl" type="text" />
      </Form.Group>
      <ButtonToolbar>
        <Button
          appearance="primary"
          onClick={() => handleSave()}
          loading={state.status === 'saving'}
          type="submit"
        >
          Save
        </Button>
        <Button appearance="ghost" onClick={() => onCancel()}>
          Cancel
        </Button>
      </ButtonToolbar>
    </Form>
  );
}

EshopSetupAllegroResponsibleProducerEdit.defaultProps = {
  responsibleProducer: undefined,
};
